import { createSelector } from './reselect';
import {
  getNewStats,
  safeDivide,
  simpleChart,
  simpleChart2,
  simpleChart3,
  total,
} from './generalUtils';

import amazon from './services/amazon';
const {
  amazonNetProfit,
  totalAmazonOrders,
  chartAmazonOrders,
  totalAmazonSales,
  chartAmazonSales,
  cahrtAmazonNetProfit,
  amazonNetSales,
  chartAmazonNetSales,
  amazonAOV,
  amazonAOVChart,
} = amazon.SELECTORS;

import {
  totalShopifyOrders,
  chartShopifyOrders,
  totalShopifySales,
  chartShopifySales,
  chartShopifyOrdersWithAmount,
} from './generalSelectors';

// spend
const spend = createSelector([getNewStats, (state, serviceId) => serviceId], (stats, serviceId) => {
  return total(stats[serviceId], 'spend');
});

const spendChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'spend');
  }
);

// purchases
const metaPurchases = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'metaPurchases');
  }
);

const metaPurchasesChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'metaPurchases');
  }
);

const webPurchases = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'purchases');
  }
);

const webPurchasesChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'purchases');
  }
);

const purchases = createSelector(
  [
    getNewStats,
    (state, serviceId) => webPurchases(state, serviceId),
    (state, serviceId) => metaPurchases(state, serviceId),
    (state, serviceId) => serviceId,
  ],
  (stats, web, meta, serviceId) => {
    if (serviceId === 'facebook-ads') return web + meta;
    return total(stats[serviceId], 'purchases');
  }
);

const purchasesChart = createSelector(
  [
    getNewStats,
    (state, serviceId) => webPurchasesChart(state, serviceId),
    (state, serviceId) => metaPurchasesChart(state, serviceId),
    (state, serviceId) => serviceId,
  ],
  (stats, web, meta, serviceId) => {
    if (serviceId === 'facebook-ads') return simpleChart3(web, meta);
    return simpleChart(stats[serviceId], 'purchases');
  }
);

//Impressions
const impressions = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'impressions');
  }
);

const impressionsChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'impressions');
  }
);

//ConversionValue
const webConversionValue = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'conversionValue');
  }
);

const webConversionValueChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'conversionValue');
  }
);

const metaConversionValue = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'metaConversionValue');
  }
);

const metaConversionValueChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'metaConversionValue');
  }
);

const conversionValue = createSelector(
  [
    getNewStats,
    (state, serviceId) => webConversionValue(state, serviceId),
    (state, serviceId) => metaConversionValue(state, serviceId),
    (state, serviceId) => serviceId,
  ],
  (stats, web, meta, serviceId) => {
    if (serviceId === 'facebook-ads') return web + meta;
    return total(stats[serviceId], 'conversionValue');
  }
);

const conversionValueChart = createSelector(
  [
    getNewStats,
    (state, serviceId) => webConversionValueChart(state, serviceId),
    (state, serviceId) => metaConversionValueChart(state, serviceId),
    (state, serviceId) => serviceId,
  ],
  (stats, web, meta, serviceId) => {
    if (serviceId === 'facebook-ads') return simpleChart3(web, meta);
    return simpleChart(stats[serviceId], 'conversionValue');
  }
);

//one day conversionValue
const dayViewPurchaseConversionValue = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'oneDayViewConversionValue');
  }
);

//clicks
const clicks = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'clicks');
  }
);

const clicksChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'clicks');
  }
);

//outboundClicks
const outboundClicks = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'outboundClicks');
  }
);

const outboundClicksChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'outboundClicks');
  }
);

//video
const totalVideoPlays = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'totalVideoView');
  }
);

const threeSecondVideoView = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'threeSecondVideoView');
  }
);

// cpc
const averageCpc = createSelector(
  [(state, serviceId) => clicks(state, serviceId), (state, serviceId) => spend(state, serviceId)],
  (clicks, spend) => {
    return safeDivide(spend, clicks);
  }
);

const cpcChart = createSelector(
  [
    (state, serviceId) => clicksChart(state, serviceId),
    (state, serviceId) => spendChart(state, serviceId),
  ],
  (clicks, spend) => {
    return simpleChart2(spend, clicks);
  }
);

// cpoc
const costPerOutboundClick = createSelector(
  [
    (state, serviceId) => outboundClicks(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (outboundClicks, spend) => {
    return safeDivide(spend, outboundClicks);
  }
);

const costPerOutboundClickChart = createSelector(
  [
    (state, serviceId) => outboundClicksChart(state, serviceId),
    (state, serviceId) => spendChart(state, serviceId),
  ],
  (outboundClicks, spend) => {
    return simpleChart2(spend, outboundClicks);
  }
);

//VT
const viewThrough = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return total(stats[serviceId], 'twViewThrough');
  }
);

const viewThroughChart = createSelector(
  [getNewStats, (state, serviceId) => serviceId],
  (stats, serviceId) => {
    return simpleChart(stats[serviceId], 'twViewThrough');
  }
);

const viewThroughRoas = createSelector(
  [
    (state, serviceId) => viewThrough(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (viewCV, spend) => {
    return safeDivide(viewCV, spend);
  }
);

const viewThroughRoasChart = createSelector(
  [
    (state, serviceId) => viewThroughChart(state, serviceId),
    (state, serviceId) => spendChart(state, serviceId),
  ],
  (viewCV, spend) => {
    return simpleChart2(viewCV, spend);
  }
);

//TODO
const viewThroughCpa = createSelector(
  [
    (state, serviceId) => viewThrough(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (viewCV, spend) => {
    return 0;
  }
);

const viewThroughCpaChart = createSelector(
  [
    (state, serviceId) => viewThroughChart(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (viewCV, spend) => {
    return [];
  }
);

// ctr
const ctr = createSelector(
  [
    (state, serviceId) => clicks(state, serviceId),
    (state, serviceId) => impressions(state, serviceId),
  ],
  (clicks, impressions) => {
    return safeDivide(clicks, impressions) * 100;
  }
);

// oc ctr
const outboundClicksCtr = createSelector(
  [
    (state, serviceId) => outboundClicks(state, serviceId),
    (state, serviceId) => impressions(state, serviceId),
    getNewStats,
  ],
  (outboundClicks, impressions, stats) => {
    if (!stats.length) return 0;
    return safeDivide(outboundClicks, impressions) * 100;
  }
);

//facebookConversionPixelPurchase
//facebookConversionPixelPurchaseValue

// cpm
const cpm = createSelector(
  [
    (state, serviceId) => impressions(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (impressions, spend) => {
    return safeDivide(spend, impressions / 1000);
  }
);

const cpmChart = createSelector(
  [
    (state, serviceId) => impressionsChart(state, serviceId),
    (state, serviceId) => spendChart(state, serviceId),
  ],
  (impressions, spend) => {
    return simpleChart2(spend, impressions, { chart1yMultiplier: 1000 });
  }
);

const ctrChart = createSelector(
  [
    (state, serviceId) => clicksChart(state, serviceId),
    (state, serviceId) => impressionsChart(state, serviceId),
  ],
  (clicks, impressions) => {
    return simpleChart2(clicks, impressions, { chart1yMultiplier: 100 });
  }
);

const averagePurchaseRoas = createSelector(
  [
    (state, serviceId) => conversionValue(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (conversionValue, spend) => {
    return safeDivide(conversionValue, spend);
  }
);

const averageDayViewPurchaseRoas = createSelector(
  [
    (state, serviceId) => dayViewPurchaseConversionValue(state, serviceId),
    (state, serviceId) => spend(state, serviceId),
  ],
  (conversionValue, spend) => {
    return safeDivide(conversionValue, spend);
  }
);

const chartPurchaseRoas = createSelector(
  [
    (state, serviceId) => conversionValueChart(state, serviceId),
    (state, serviceId) => spendChart(state, serviceId),
  ],
  (conversionValue, spend) => {
    return simpleChart2(conversionValue, spend);
  }
);

// cpa
const cpaChart = createSelector(
  [
    (state, serviceId) => spendChart(state, serviceId),
    (state, serviceId) => purchasesChart(state, serviceId),
  ],
  (spendChart, purchasesChart) => simpleChart2(spendChart, purchasesChart)
);

//aov
const aovChart = createSelector(
  [
    (state, serviceId) => conversionValueChart(state, serviceId),
    (state, serviceId) => purchasesChart(state, serviceId),
  ],
  (spendChart, purchasesChart) => simpleChart2(spendChart, purchasesChart)
);

// shop generic metrics
const orders = createSelector(
  [totalShopifyOrders, totalAmazonOrders, (state, serviceId) => serviceId],
  (shopifyOrders, amazonOrders, serviceId) => {
    if (serviceId === 'amazon') {
      return amazonOrders;
    } else if (serviceId === 'shopify') {
      return shopifyOrders;
    } else {
      return 0;
    }
  }
);

const ordersChart = createSelector(
  [chartShopifyOrders, chartAmazonOrders, (state, serviceId) => serviceId],
  (chartShopifyOrders, chartAmazonOrders, serviceId) => {
    if (serviceId === 'amazon') {
      return chartAmazonOrders;
    } else if (serviceId === 'shopify') {
      return chartShopifyOrders;
    } else {
      return [];
    }
  }
);

const sales = createSelector(
  [totalShopifySales, totalAmazonSales, (state, serviceId) => serviceId],
  (shopifySales, amazonSales, serviceId) => {
    if (serviceId === 'amazon') {
      return amazonSales;
    } else if (serviceId === 'shopify') {
      return shopifySales;
    } else {
      return 0;
    }
  }
);

const salesChart = createSelector(
  [chartShopifySales, chartAmazonSales, (state, serviceId) => serviceId],
  (chartShopifySales, chartAmazonSales, serviceId) => {
    if (serviceId === 'amazon') {
      return chartAmazonSales;
    } else if (serviceId === 'shopify') {
      return chartShopifySales;
    } else {
      return [];
    }
  }
);

//moved to index bc of circular dependency
// const generalNetProfit = createSelector(
//   [shopifyNetProfit, amazonNetProfit, (state, serviceId) => serviceId],
//   (sh, am, serviceId) => {
//     if (serviceId === 'amazon') {
//       return am;
//     } else if (serviceId === 'shopify') {
//       return sh;
//     } else {
//       return 0;
//     }
//   }
// );

// const netProfitChart = createSelector(
//   [
//     totalNetProfitChart,
//     cahrtAmazonNetProfit,
//     (state, serviceId) => serviceId,
//   ],
//   (chartShopifyNetProfit, chartAmazonNetProfit, serviceId) => {
//     if (serviceId === 'amazon') {
//       return chartAmazonNetProfit;
//     } else if (serviceId === 'shopify') {
//       return chartShopifyNetProfit;
//     } else {
//       return [];
//     }
//   }
// );

// const generalNetSales = createSelector(
//   [netSales, amazonNetSales, (state, serviceId) => serviceId],
//   (shopifyNetSales, amazonNetSales, serviceId) => {
//     if (serviceId === 'amazon') {
//       return amazonNetSales;
//     } else if (serviceId === 'shopify') {
//       return shopifyNetSales;
//     } else {
//       return 0;
//     }
//   }
// );

// const generalNetSalesChart = createSelector(
//   [netSalesChart, chartAmazonNetSales, (state, serviceId) => serviceId],
//   (shopifyNetSalesChart, chartAmazonNetSales, serviceId) => {
//     if (serviceId === 'amazon') {
//       return chartAmazonNetSales;
//     } else if (serviceId === 'shopify') {
//       return shopifyNetSalesChart;
//     } else {
//       return [];
//     }
//   }
// );

// const averageOrderValue = createSelector(
//   [shopifyAov, amazonAOV, (state, serviceId) => serviceId],
//   (shopifyAOV, amazonAOV, serviceId) => {
//     if (serviceId === 'amazon') {
//       return amazonAOV;
//     } else if (serviceId === 'shopify') {
//       return shopifyAOV;
//     } else {
//       return 0;
//     }
//   }
// );

// const averageOrderValueChart = createSelector(
//   [chartShopifyAov, amazonAOVChart, (state, serviceId) => serviceId],
//   (chartShopifyAov, amazonAOVChart, serviceId) => {
//     if (serviceId === 'amazon') {
//       return amazonAOVChart;
//     } else if (serviceId === 'shopify') {
//       return chartShopifyAov;
//     } else {
//       return [];
//     }
//   }
// );

//facebookConversionValue
//facebookDayViewConversionValue

export default {
  SELECTORS: {
    spend,
    spendChart,
    metaPurchases,
    metaPurchasesChart,
    webPurchases,
    webPurchasesChart,
    purchases,
    purchasesChart,
    outboundClicks,
    outboundClicksChart,
    impressions,
    impressionsChart,
    webConversionValue,
    webConversionValueChart,
    metaConversionValue,
    metaConversionValueChart,
    dayViewPurchaseConversionValue,
    clicks,
    clicksChart,
    totalVideoPlays,
    threeSecondVideoView,
    averageCpc,
    cpcChart,
    costPerOutboundClick,
    costPerOutboundClickChart,
    viewThrough,
    viewThroughChart,
    viewThroughRoas,
    viewThroughRoasChart,
    viewThroughCpa,
    viewThroughCpaChart,
    ctr,
    outboundClicksCtr,
    cpm,
    cpmChart,
    ctrChart,
    averagePurchaseRoas,
    averageDayViewPurchaseRoas,
    chartPurchaseRoas,
    conversionValue,
    conversionValueChart,
    cpaChart,
    aovChart,
    orders,
    ordersChart,
    sales,
    salesChart,
    // generalNetProfit,
    // netProfitChart,
    // generalNetSales,
    // generalNetSalesChart,
    // averageOrderValue,
    // averageOrderValueChart
  },
};
